import { CheckIcon, Clock, X } from 'lucide-react'

import { Badge } from 'slab'
import { type AdminComplianceAudit, type AdminRuleResult } from 'utils/web_api.schemas'

interface RuleResultDisplayProps {
  ruleResults: AdminRuleResult[]
  title: string
  showAsPending?: boolean
}

const RuleResultDisplay = ({ ruleResults, title, showAsPending = false }: RuleResultDisplayProps) => {
  if (ruleResults.length === 0) {
    return null
  }

  return (
    <div className='space-y-2'>
      <h4 className='text-sm font-medium text-gray-700'>{title}</h4>
      <div className='space-y-1'>
        {ruleResults.map((ruleResult, index) => (
          <div key={index} className='flex items-center space-x-2'>
            {showAsPending
              ? (
                <Clock className='h-4 w-4 text-yellow-500' />
                )
              : ruleResult.passes
                ? (
                  <CheckIcon className='h-4 w-4 text-green-500' />
                  )
                : (
                  <X className='h-4 w-4 text-red-500' />
                  )}
            <span className='text-sm text-gray-600 capitalize'>
              {ruleResult.rule.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface AuditCardProps {
  audit: AdminComplianceAudit
  className?: string
}

const AuditCard = ({ audit, className }: AuditCardProps) => {
  const getStateBadgeType = (state: string) => {
    switch (state) {
      case 'pending':
        return 'warning'
      case 'passed':
        return 'success'
      case 'failed':
        return 'critical'
      case 'cancelled':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  // Show requirements as pending only if not all progress rules are passing
  const showRequirementsAsPending = !(audit.progress_rule_results.every(rule => rule.passes))

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${className ?? ''}`}>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-lg font-medium text-gray-900'>{audit.partner_name}</h3>
        <Badge type={getStateBadgeType(audit.state)}>
          {audit.state.charAt(0).toUpperCase() + audit.state.slice(1)}
        </Badge>
      </div>
      <div className='space-y-4'>
        <RuleResultDisplay
          ruleResults={audit.final_rule_results}
          title='Requirements'
          showAsPending={showRequirementsAsPending}
        />
        <RuleResultDisplay
          ruleResults={audit.progress_rule_results}
          title='Progress'
        />
      </div>
    </div>
  )
}

export default AuditCard
